import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getFirestore, doc, setDoc, increment } from 'firebase/firestore';
import { functions } from './firebase';
import { Receipt, Camera, Upload, CheckCircle, ChevronRight, Plus, Trash2 } from 'lucide-react';
import './EventSubmission.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface EventInfo {
  id: string;
  name: string;
  description: string;
  venue: string;
  eventDate: number;
  totalMembers: number;
  contributorCount: number;
  eventCode: string;
  status: 'open' | 'locked';
}

interface LineItem {
  id: string;
  description: string;
  amount: number;
  isGroup: boolean;
}

type Step = 'loading' | 'error' | 'name' | 'upload' | 'uploading' | 'review' | 'submitting' | 'success';

// ── Cloud Function callers ────────────────────────────────────────────────────

const getGroupEventByCode = httpsCallable<{ eventCode: string }, EventInfo>(
  functions,
  'getGroupEventByCode'
);

const extractGroupReceiptItems = httpsCallable<
  { imageBase64: string; eventCode: string },
  { items: LineItem[]; rawText?: string }
>(functions, 'extractGroupReceiptItems');

const submitGroupContributor = httpsCallable<
  { eventCode: string; name: string; items: LineItem[]; receiptImageBase64?: string; paymentHandle?: string },
  { success: boolean; contributorName: string; groupTotal: number; personalTotal: number; itemCount: number }
>(functions, 'submitGroupContributor');

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMoney(n: number): string {
  return `$${n.toFixed(2)}`;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]); // strip data:...;base64,
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function uid(): string {
  return `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function EventSubmission() {
  const { eventCode } = useParams<{ eventCode: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [event, setEvent] = useState<EventInfo | null>(null);
  const [memberName, setMemberName] = useState('');
  const [paymentHandle, setPaymentHandle] = useState('');
  const [items, setItems] = useState<LineItem[]>([]);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<{ groupTotal: number; personalTotal: number } | null>(null);

  const [countdown, setCountdown] = useState(5);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Fetch event on mount
  useEffect(() => {
    if (!eventCode) {
      setErrorMsg('No event code in URL.');
      setStep('error');
      return;
    }

    getGroupEventByCode({ eventCode: eventCode.toUpperCase() })
      .then((res) => {
        // Smart link: if submissions are closed, go straight to settlement
        if (res.data.status === 'locked') {
          navigate(`/event/${eventCode.toUpperCase()}/settle`, { replace: true });
          return;
        }
        setEvent(res.data);
        setStep('name');
        const db = getFirestore();
        setDoc(doc(db, 'analytics', 'siteStats'), { memberPageViews: increment(1) }, { merge: true })
          .catch(() => {});
      })
      .catch((err) => {
        setErrorMsg(
          err.code === 'functions/not-found'
            ? `No event found for code ${eventCode.toUpperCase()}.`
            : 'Failed to load event. Please check the link and try again.'
        );
        setStep('error');
      });
  }, [eventCode]);

  // Auto-redirect to settlement after successful submission
  useEffect(() => {
    if (step !== 'success' || !event) return;
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          window.location.href = `/event/${event.eventCode}/settle?name=${encodeURIComponent(memberName)}`;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [step, event, memberName]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleNameContinue() {
    if (memberName.trim().length < 1) return;
    setStep('upload');
  }

  async function handleFileSelected(file: File) {
    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
    setStep('uploading');

    try {
      const base64 = await toBase64(file);
      const res = await extractGroupReceiptItems({
        imageBase64: base64,
        eventCode: eventCode!.toUpperCase(),
      });

      if (res.data.items.length > 0) {
        setItems(res.data.items);
      } else {
        // No items extracted → give one blank row so user isn't stuck
        setItems([{ id: uid(), description: '', amount: 0, isGroup: true }]);
      }
      setStep('review');
    } catch {
      // OCR failed → let user enter manually
      setItems([{ id: uid(), description: '', amount: 0, isGroup: true }]);
      setStep('review');
    }
  }

  function handleSkipUpload() {
    setReceiptFile(null);
    setReceiptPreview(null);
    setItems([{ id: uid(), description: '', amount: 0, isGroup: true }]);
    setStep('review');
  }

  function toggleItemGroup(id: string) {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isGroup: !item.isGroup } : item))
    );
  }

  function setAllGroup(isGroup: boolean) {
    setItems((prev) => prev.map((item) => ({ ...item, isGroup })));
  }

  function updateItem(id: string, field: 'description' | 'amount', value: string) {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value }
          : item
      )
    );
  }

  function addItem() {
    setItems((prev) => [...prev, { id: uid(), description: '', amount: 0, isGroup: true }]);
  }

  function addTax() {
    setItems((prev) => [...prev, { id: uid(), description: 'Tax', amount: 0, isGroup: true }]);
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }

  async function handleSubmit() {
    const validItems = items.filter((i) => i.description.trim().length > 0 && i.amount > 0);
    if (validItems.length === 0) return;

    setStep('submitting');
    try {
      let imageBase64: string | undefined;
      if (receiptFile) {
        imageBase64 = await toBase64(receiptFile);
      }

      const res = await submitGroupContributor({
        eventCode: eventCode!.toUpperCase(),
        name: memberName.trim(),
        items: validItems,
        receiptImageBase64: imageBase64,
        paymentHandle: paymentHandle.trim() || undefined,
      });

      setSubmitResult({ groupTotal: res.data.groupTotal, personalTotal: res.data.personalTotal });
      setStep('success');
    } catch (err: any) {
      setErrorMsg(err.message || 'Submission failed. Please try again.');
      setStep('error');
    }
  }

  async function handleCheckin() {
    setStep('submitting');
    try {
      const res = await submitGroupContributor({
        eventCode: eventCode!.toUpperCase(),
        name: memberName.trim(),
        items: [],
        paymentHandle: paymentHandle.trim() || undefined,
      });
      setSubmitResult({ groupTotal: res.data.groupTotal, personalTotal: res.data.personalTotal });
      setStep('success');
    } catch (err: any) {
      setErrorMsg(err.message || 'Check-in failed. Please try again.');
      setStep('error');
    }
  }

  // ── Computed ─────────────────────────────────────────────────────────────────

  const groupTotal = items.filter((i) => i.isGroup).reduce((s, i) => s + i.amount, 0);
  const personalTotal = items.filter((i) => !i.isGroup).reduce((s, i) => s + i.amount, 0);
  const validItemCount = items.filter((i) => i.description.trim().length > 0 && i.amount > 0).length;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="es-page">
      {/* Header */}
      <header className="es-header">
        <div className="es-logo">
          <div className="es-logo-icon">
            <Receipt size={18} color="white" strokeWidth={2} />
          </div>
          <span className="es-logo-text">XpenseMate</span>
        </div>
        {event && <span className="es-event-code">{event.eventCode}</span>}
      </header>

      <main className="es-main">
        {/* ── Loading ── */}
        {step === 'loading' && (
          <div className="es-center">
            <div className="es-spinner" />
            <p className="es-muted">Loading event…</p>
          </div>
        )}

        {/* ── Error ── */}
        {step === 'error' && (
          <div className="es-center">
            <div className="es-error-icon">✗</div>
            <p className="es-error-msg">{errorMsg}</p>
          </div>
        )}

        {/* ── Step 1: Enter name ── */}
        {step === 'name' && event && (
          <div className="es-card-stack">
            <div className="es-event-card">
              <h2 className="es-event-name">{event.name}</h2>
              {event.venue && <p className="es-event-meta">{event.venue}</p>}
              <p className="es-event-meta">{formatDate(event.eventDate)}</p>
              <p className="es-event-meta">
                {event.contributorCount} of {event.totalMembers} members submitted
              </p>
            </div>

            <div className="es-section">
              <label className="es-label">Your name</label>
              <input
                className="es-input"
                type="text"
                placeholder="Enter your name"
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNameContinue()}
                autoFocus
              />
              <label className="es-label es-label-optional">
                Your payment handle <span className="es-optional-tag">optional</span>
              </label>
              <input
                className="es-input"
                type="text"
                placeholder="@venmo, $cashapp, or paypal.me/you"
                value={paymentHandle}
                onChange={(e) => setPaymentHandle(e.target.value)}
              />
              <p className="es-handle-hint">So the organizer can pay you back if you're owed money</p>
              <button
                className="es-btn-primary"
                onClick={handleNameContinue}
                disabled={memberName.trim().length < 1}
              >
                Continue <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Upload receipt ── */}
        {step === 'upload' && event && (
          <div className="es-card-stack">
            <div className="es-step-header">
              <button className="es-back" onClick={() => setStep('name')}>← Back</button>
              <p className="es-step-title">Upload receipt</p>
            </div>

            <div
              className="es-upload-zone"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file && file.type.startsWith('image/')) handleFileSelected(file);
              }}
            >
              <Upload size={36} className="es-upload-icon" />
              <p className="es-upload-primary">Tap to upload a receipt</p>
              <p className="es-upload-secondary">or drag and drop an image</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelected(file);
                }}
              />
            </div>

            <button
              className="es-btn-camera"
              onClick={() => cameraInputRef.current?.click()}
            >
              <Camera size={18} /> Take a photo
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelected(file);
                }}
              />
            </button>

            <button className="es-btn-ghost" onClick={handleSkipUpload}>
              Skip — I'll enter items manually
            </button>

            <div className="es-checkin-divider">
              <span>or</span>
            </div>

            <button className="es-btn-checkin" onClick={handleCheckin}>
              I didn't pay for anything — just check me in
            </button>
          </div>
        )}

        {/* ── Step 2b: OCR in progress ── */}
        {step === 'uploading' && (
          <div className="es-center">
            {receiptPreview && (
              <img src={receiptPreview} className="es-receipt-thumb" alt="Receipt preview" />
            )}
            <div className="es-spinner" />
            <p className="es-muted">Extracting line items…</p>
          </div>
        )}

        {/* ── Step 3: Review items ── */}
        {step === 'review' && event && (
          <div className="es-card-stack">
            <div className="es-step-header">
              <button className="es-back" onClick={() => setStep('upload')}>← Back</button>
              <p className="es-step-title">Review items</p>
            </div>

            {receiptPreview && (
              <img src={receiptPreview} className="es-receipt-thumb" alt="Receipt" />
            )}

            {/* Select-all bar */}
            <div className="es-select-all-bar">
              <span className="es-select-all-label">Mark all as:</span>
              <button
                className="es-select-all-btn group"
                onClick={() => setAllGroup(true)}
              >
                All Group
              </button>
              <button
                className="es-select-all-btn personal"
                onClick={() => setAllGroup(false)}
              >
                All Personal
              </button>
            </div>

            <div className="es-items-list">
              {items.map((item) => (
                <div key={item.id} className="es-item-row">
                  <div className="es-item-fields">
                    <input
                      className="es-item-desc"
                      type="text"
                      placeholder="Item description"
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                    />
                    <div className="es-item-amount-row">
                      <span className="es-dollar">$</span>
                      <input
                        className="es-item-amount"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={item.amount || ''}
                        onChange={(e) => updateItem(item.id, 'amount', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="es-item-actions">
                    <div className="es-toggle">
                      <button
                        className={`es-toggle-btn ${item.isGroup ? 'active' : ''}`}
                        onClick={() => !item.isGroup && toggleItemGroup(item.id)}
                      >
                        Group
                      </button>
                      <button
                        className={`es-toggle-btn ${!item.isGroup ? 'active personal' : ''}`}
                        onClick={() => item.isGroup && toggleItemGroup(item.id)}
                      >
                        Personal
                      </button>
                    </div>
                    <button
                      className="es-remove-btn"
                      onClick={() => removeItem(item.id)}
                      aria-label="Remove item"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="es-add-row">
              <button className="es-btn-add" onClick={addItem}>
                <Plus size={15} /> Add item
              </button>
              <button className="es-btn-add tax" onClick={addTax}>
                <Plus size={15} /> Add tax / tip
              </button>
            </div>

            <div className="es-totals">
              <div className="es-total-row">
                <span>Group total</span>
                <span className="es-total-amount group">{formatMoney(groupTotal)}</span>
              </div>
              <div className="es-total-row">
                <span>Personal total</span>
                <span className="es-total-amount personal">{formatMoney(personalTotal)}</span>
              </div>
            </div>

            <button
              className="es-btn-primary"
              onClick={handleSubmit}
              disabled={validItemCount === 0}
            >
              Submit to pool
            </button>
          </div>
        )}

        {/* ── Step 4: Submitting ── */}
        {step === 'submitting' && (
          <div className="es-center">
            <div className="es-spinner" />
            <p className="es-muted">Submitting…</p>
          </div>
        )}

        {/* ── Step 5: Success ── */}
        {step === 'success' && event && submitResult && (
          <div className="es-center">
            <CheckCircle size={56} className="es-success-icon" />
            <h2 className="es-success-title">Submitted!</h2>
            <p className="es-success-name">{memberName}</p>

            <div className="es-success-totals">
              <div className="es-success-row">
                <span>Group expenses</span>
                <span className="es-total-amount group">{formatMoney(submitResult.groupTotal)}</span>
              </div>
              {submitResult.personalTotal > 0 && (
                <div className="es-success-row">
                  <span>Personal expenses</span>
                  <span className="es-total-amount personal">{formatMoney(submitResult.personalTotal)}</span>
                </div>
              )}
            </div>

            <p className="es-success-note">
              Your group items have been added to the <strong>{event.name}</strong> pool.
            </p>
            <a
              href={`/event/${event.eventCode}/settle?name=${encodeURIComponent(memberName)}`}
              className="es-btn-primary es-btn-settle"
              style={{ textDecoration: 'none', marginTop: '16px' }}
            >
              See what you owe →
            </a>
            <p className="es-countdown">
              Taking you there in <strong>{countdown}s</strong>…
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
